import { useEffect, useRef, useState } from 'react';

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/**
 * When `target` increases, tweens the displayed integer upward. Snaps on decrease, first load, or reduced motion.
 * @param {number | null | undefined} target
 * @param {number} [durationMs]
 * @returns {number | null} rounded value to format for display
 */
export function useAnimatedLeaderboardScore(target, durationMs = 1000) {
  const reducedMotion = usePrefersReducedMotion();
  const [display, setDisplay] = useState(null);
  const displayRef = useRef(null);
  displayRef.current = display;
  const prevTargetRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (target == null || typeof target !== 'number' || !Number.isFinite(target)) {
      setDisplay(null);
      prevTargetRef.current = null;
      return undefined;
    }

    const next = Math.round(target);
    const prevT = prevTargetRef.current;

    if (prevT === null) {
      setDisplay(next);
      prevTargetRef.current = next;
      return undefined;
    }

    if (next === prevT) {
      return undefined;
    }

    if (next < prevT || reducedMotion) {
      setDisplay(next);
      prevTargetRef.current = next;
      return undefined;
    }

    prevTargetRef.current = next;
    const from = displayRef.current != null ? displayRef.current : prevT;
    if (from >= next) {
      setDisplay(next);
      return undefined;
    }

    const start = performance.now();
    const delta = next - from;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = easeOutCubic(t);
      const v = Math.round(from + delta * eased);
      setDisplay(v);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(next);
        rafRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [target, durationMs, reducedMotion]);

  return display;
}
